<!--
/**
 * FormKitFundAccountSelect
 *
 * Custom FormKit input that wraps PrimeVue AutoComplete for fund account selection.
 * Fetches options via useBffClient().getFundAccounts() with search and optional
 * admin-fee filter. Supports dual mode: inside a FormKit form (context) or standalone with v-model.
 *
 * @description Fund account autocomplete with server-side search and bounded in-memory cache.
 *
 * FormKit usage (e.g. in a form):
 *   <FormKit type="fundAccountSelect" name="fundAccountId" label="Fund account"
 *            :admin-fee-issue-filter="true" placeholder="Select fund account..."
 *            v-model="form.fundAccountId" />
 *
 * Standalone usage (e.g. in a card/record view):
 *   <FormKitFundAccountSelect v-model="selectedFundAccountId" placeholder="Select fund account..."
 *                             :disabled="false" />
 *
 * @prop {Object} [context] - FormKit framework context (provided when used as FormKit input).
 *   Context may include: placeholder, adminFeeIssueFilter, pageSize, disabled.
 * @prop {string} [modelValue=''] - Selected fund account id (string).
 * @prop {string} [placeholder=''] - Placeholder text when nothing selected. Empty = non-breaking space.
 * @prop {boolean} [disabled=false] - Disable the input.
 * @emits {string} update:modelValue - Emitted with the selected fund account id when selection changes.
 */
-->
<template>
  <div v-bind="attrs" :aria-required="isRequired" :class="wrapperClass">
    <AutoComplete
      :input-id="inputId"
      :model-value="displayValue"
      :suggestions="suggestions"
      option-label="label"
      option-value="id"
      :placeholder="displayPlaceholder"
      :disabled="disabled"
      :show-clear="true"
      complete-on-focus
      class="w-full"
      data-testid="formkit-fund-account-select-input"
      @complete="onSearch"
      @update:model-value="onSelect"
    />
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { computed } from 'vue'
import type { FormKitFrameworkContext } from '@formkit/core'
import type { FundAccount } from '~/models/graphql/generated/types'
import {
  useFormKitAutoComplete,
  type AutoCompleteOption,
} from '~/composables/useFormKitAutoComplete'
import { fundAccountLabel } from '~/services/fund-account-label'

export type FundAccountOption = AutoCompleteOption

const PAGE_SIZE = 25
const MAX_PAGES_FOR_ID_LOOKUP = 10

const props = defineProps({
  context: {
    type: Object as PropType<
      FormKitFrameworkContext & {
        adminFeeIssueFilter?: boolean
        placeholder?: string
        pageSize?: number
        disabled?: boolean
      }
    >,
    default: undefined,
  },
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const bffClient = useBffClient()

const adminFeeIssueFilter = computed(
  () => props.context?.adminFeeIssueFilter ?? false
)
const pageSize = computed(() => props.context?.pageSize ?? PAGE_SIZE)

function optionFromFundAccount(fa: FundAccount): FundAccountOption {
  return { id: fa.id, label: fundAccountLabel(fa) }
}

async function search(query: string): Promise<FundAccountOption[]> {
  try {
    const searchQuery = query?.trim() || undefined
    const response = await bffClient.getFundAccounts(
      1,
      pageSize.value,
      searchQuery,
      adminFeeIssueFilter.value
    )
    return response.collection.map(optionFromFundAccount)
  } catch {
    return []
  }
}

async function loadById(id: string): Promise<FundAccountOption | null> {
  try {
    for (let page = 1; page <= MAX_PAGES_FOR_ID_LOOKUP; page++) {
      const response = await bffClient.getFundAccounts(
        page,
        pageSize.value,
        undefined,
        adminFeeIssueFilter.value
      )
      const fa = response.collection.find((x) => x.id === id)
      if (fa) return optionFromFundAccount(fa)
      if (page >= response.metadata.totalPages) break
    }
  } catch {
    // Leave display unchanged so existing label can remain
  }
  return null
}

const {
  suggestions,
  displayValue,
  displayPlaceholder,
  disabled,
  inputId,
  isRequired,
  attrs,
  onSearch,
  onSelect,
  wrapperClass,
} = useFormKitAutoComplete({ props, emit, search, loadById })
</script>
